// Global Instructions Rule Applied!
//
// The distribution form's "Taken first" section (design doc Q3, priority
// tiers): what is paid ahead of the LPA tiers and the checks that gate an
// SBIC distribution.
//   * the priority lines (the special distribution of waived SBIC fees:
//     fee__waived_fees.outstanding per LP, SBIC §3.05(f)(iii)), each with its
//     own override in the Calculated / Override / Effective grammar - they
//     are per row, not attributed: the Allocation grid only totals them,
//   * the SBA gates as Compute found them (SBIC LPA §7.01(a)): a Tag per
//     gate, green Passed / gold Failed, the figures in its detail. The owner's
//     answer 5: warn and record the override with a reason - a failed gate
//     needs the reason before Issue; nothing is refused on a data gap,
//   * the lender-repayment hook of a Deal-by-Deal fund (A3.8 decision 3):
//     not computed, the read it needs does not exist yet.

import React, { useMemo } from 'react'
import { Alert, Input, InputNumber, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import NumberText from '../../../../core/components/NumberText.tsx'
import type { useNumberFormat } from '../../../../core/utils/inputNumberFormat.ts'
import type {
  DistributionDetail,
  DistributionPerCommitment,
  DistributionSbaCheck
} from '../../../../state_manager/data_models/distribution_detail.ts'
import { CALCULATED_TITLE, EFFECTIVE_TITLE, OVERRIDE_TITLE, rowTierLabel } from './allocationLabels.ts'

const { Text } = Typography

/** The override as it stands for a priority row: this session's edit, else the saved one. */
export type OverrideReader = (row: DistributionPerCommitment) => number | null
export type OverrideWriter = (rowId: string, value: number | null) => void

interface TakenFirstPanelProps {
  detail: DistributionDetail | null
  reason: string
  onReasonChange: (value: string) => void
  readOnly: boolean
  /** the Override inputs render disabled with this reason (a consolidated distribution: none in this phase) */
  overridesLockedReason?: string | null
  pcOverride: OverrideReader
  setPcOverride: OverrideWriter
  currencyFmt: ReturnType<typeof useNumberFormat>
}

/** The amount Issue books for a priority row: the override if there is one, else the calculated amount. */
export function rowEffective(row: DistributionPerCommitment, pcOverride: OverrideReader): number | null {
  return pcOverride(row) ?? row.calculated_amount ?? null
}

export default function TakenFirstPanel({
  detail,
  reason,
  onReasonChange,
  readOnly,
  overridesLockedReason,
  pcOverride,
  setPcOverride,
  currencyFmt
}: TakenFirstPanelProps) {
  const priorityRows = useMemo(
    () => (detail?.per_commitment ?? []).filter((r) => r.kind === 'priority'),
    [detail]
  )
  const checks: DistributionSbaCheck[] = detail?.distribution?.sba_checks ?? []
  const isSbic = detail?.distribution?.fund_is_sbic === true
  const computed = Boolean(detail?.distribution?.computed_at)
  const failed = checks.filter((c) => !c.passed)
  const settles = detail?.distribution?.prog__settles_by_special_distribution === true
  const dealByDeal = detail?.distribution?.prog__scope_label === 'Deal-by-Deal'

  const priorityColumns: ColumnsType<DistributionPerCommitment> = [
    { title: 'Investor', dataIndex: 'investors__name', render: (v: string | null) => v ?? '-' },
    { title: 'Line', key: 'line', render: (_: unknown, row) => rowTierLabel(row) },
    {
      title: CALCULATED_TITLE,
      dataIndex: 'calculated_amount',
      width: 160,
      align: 'right',
      render: (v: number | null | undefined, row) =>
        pcOverride(row) != null ? (
          <NumberText value={v} wrap={(f) => <Text delete type='secondary'>{f}</Text>} />
        ) : (
          <NumberText value={v} />
        )
    },
    {
      title: OVERRIDE_TITLE,
      key: 'override',
      width: 170,
      align: 'right',
      render: (_: unknown, row) =>
        readOnly ? (
          pcOverride(row) != null ? <NumberText value={pcOverride(row)} /> : null
        ) : (
          <InputNumber
            size='small'
            className='w-full'
            aria-label={`Override taken first for ${row.investors__name ?? 'this investor'}`}
            value={pcOverride(row)}
            placeholder='-'
            disabled={overridesLockedReason != null}
            title={overridesLockedReason ?? undefined}
            status={pcOverride(row) != null ? 'warning' : undefined}
            formatter={currencyFmt.formatter}
            parser={currencyFmt.parser}
            onChange={(v) => setPcOverride(row.id, v == null ? null : Number(v))}
          />
        )
    },
    {
      title: EFFECTIVE_TITLE,
      key: 'effective',
      width: 160,
      align: 'right',
      render: (_: unknown, row) => <NumberText value={rowEffective(row, pcOverride)} wrap={(f) => <Text strong>{f}</Text>} />
    }
  ]

  const checkColumns: ColumnsType<DistributionSbaCheck> = [
    { title: 'Gate', dataIndex: 'label', width: 280, render: (v: string | null, c) => v ?? c.code },
    {
      title: 'Result',
      dataIndex: 'passed',
      width: 100,
      render: (v: boolean) => (v ? <Tag color='green'>Passed</Tag> : <Tag color='gold'>Failed</Tag>)
    },
    { title: 'Detail', dataIndex: 'detail', render: (v: string | null) => v ?? '-' }
  ]

  if (!detail?.distribution?.id) {
    return <Text type='secondary'>Save the distribution, then Compute to see what is taken first.</Text>
  }

  return (
    <div className='flex flex-col gap-3'>
      <div>
        <Text strong>Priority lines</Text>
        {!computed ? (
          <div>
            <Text type='secondary'>Compute to see the lines paid ahead of the tiers.</Text>
          </div>
        ) : priorityRows.length === 0 ? (
          <div>
            <Text type='secondary'>
              {settles
                ? 'Nothing outstanding under the special-distribution settlement as at the distribution date.'
                : 'None: the fund settles no waived fees by special distribution.'}
            </Text>
          </div>
        ) : (
          <Table<DistributionPerCommitment>
            size='small'
            rowKey='id'
            pagination={false}
            columns={priorityColumns}
            dataSource={priorityRows}
            className='mt-1'
          />
        )}
      </div>

      <div>
        <Text strong>SBA gates</Text>
        {!isSbic ? (
          <div>
            <Text type='secondary'>No SBA gates: the fund is not an SBIC.</Text>
          </div>
        ) : !computed ? (
          <div>
            <Text type='secondary'>Compute runs the SBA gates (SBIC LPA §7.01(a)) and records what it finds.</Text>
          </div>
        ) : (
          <>
            <Table<DistributionSbaCheck>
              size='small'
              rowKey='code'
              pagination={false}
              columns={checkColumns}
              dataSource={checks}
              className='mt-1'
            />
            {failed.length > 0 ? (
              <div className='mt-2'>
                <Alert
                  type='warning'
                  showIcon
                  className='mb-2'
                  message={`${failed.length} gate(s) failed - record the override reason before issuing`}
                  description='The engine never refuses on a data gap (owner, 2026-10-06): the reason is stored on the distribution and shown here.'
                />
                <div className='mb-1 text-gray-500 text-sm'>Override reason</div>
                <Input.TextArea
                  aria-label='SBA override reason'
                  rows={2}
                  value={reason}
                  disabled={readOnly}
                  placeholder='Why the distribution proceeds although a gate failed'
                  onChange={(e) => onReasonChange(e.target.value)}
                />
              </div>
            ) : reason ? (
              <div className='mt-2'>
                <Text type='secondary'>Override reason recorded: {reason}</Text>
              </div>
            ) : null}
          </>
        )}
      </div>

      {dealByDeal ? (
        <div>
          <Text strong>Lender repayment</Text>
          <div>
            <Text type='secondary'>
              Not computed: an LP whose fee-capital position is negative repays the lenders first (A3.8 decision 3),
              but the advance position read that needs does not exist yet.
            </Text>
          </div>
        </div>
      ) : null}
    </div>
  )
}
